import { apiDownload, apiRequest } from "./apiClient";

// Kategorien
export interface Category {
    id: number;
    slug: string;
    name: string;
    description?: string | null;
    group_key?: string;
    sort_order?: number;
}

// Aktueller Stand einer Uebersetzung. is_manual = true bedeutet:
// von Hand gesetzt, DeepL fasst sie nicht mehr an.
export interface EntityTranslation {
    text: string;
    is_manual: boolean;
}

export interface AdminCategory extends Category {
    translations: Record<string, EntityTranslation>;
}

export interface CategoryInput {
    name: string;
    group_key: string;
    description: string | null;
    translations?: {
        de?: string | null;
        es?: string | null;
        pt?: string | null;
    };
}

const categoryCache: Record<string, Category[]> = {};

// Holt die Kategorien einmalig und merkt sie sich (Cache).
export async function fetchCategories(lang?: string): Promise<Category[]> {
    const key = lang ?? "_";
    if (categoryCache[key]) return categoryCache[key];
    const q = lang ? `?lang=${encodeURIComponent(lang)}` : "";
    const categories = await apiRequest<Category[]>(`/api/categories${q}`);
    categoryCache[key] = categories;
    return categories;
}

// Uebersetzt einen Slug (Frontend) in die numerische Kategorie-ID (Backend).
export async function resolveCategoryId(slug: string): Promise<number> {
    const categories = await fetchCategories();
    const match = categories.find((c) => c.slug === slug);
    if (!match) throw new Error(`Unbekannte Kategorie: ${slug}`);
    return match.id;
}

// Phase / Countdown
export type AwardPhase = "IDLE" | "NOMINATION" | "VOTING";

export interface PhaseState {
    phase: AwardPhase;
    nomination_starts_at: string | null;
    nomination_ends_at: string | null;
    voting_starts_at: string | null;
    voting_ends_at: string | null;
    updated_at: string;
}

export interface ScheduleInput {
    nomination_starts_at: string | null;
    nomination_ends_at: string | null;
    voting_starts_at: string | null;
    voting_ends_at: string | null;
}

export async function fetchPhase(): Promise<PhaseState> {
    return apiRequest<PhaseState>("/api/phase");
}

// Admin-Login
export interface LoginResult {
    token: string;
    username: string;
    isOwner: boolean;
}

// Admin-Verwaltung
export interface AdminUser {
    id: number;
    username: string;
    is_owner: boolean;
    created_at: string;
}

export async function listAdmins(token: string): Promise<AdminUser[]> {
    return apiRequest<AdminUser[]>("/api/admin/admins", { token });
}

export async function createAdmin(token: string, username: string, password: string): Promise<AdminUser> {
    return apiRequest<AdminUser>("/api/admin/admins", {
        method: "POST",
        body: { username, password },
        token,
    });
}

export async function deleteAdmin(token: string, id: number): Promise<{ message: string }> {
    return apiRequest<{ message: string }>(`/api/admin/admins/${id}`, { method: "DELETE", token });
}

export async function loginAdmin(username: string, password: string): Promise<LoginResult> {
    return apiRequest<LoginResult>("/api/auth/login", {
        method: "POST",
        body: { username, password },
    });
}

// Admin: Phasen-Zeitplan
export async function setPhaseSchedule(token: string, schedule: ScheduleInput): Promise<PhaseState> {
    return apiRequest<PhaseState>("/api/admin/phase/schedule", {
        method: "PUT",
        body: schedule,
        token,
    });
}

export interface TranslationBackfillResult {
    message: string;
    content: number;
    categories: number;
}

export async function runTranslationsBackfill(token: string): Promise<TranslationBackfillResult> {
    return apiRequest<TranslationBackfillResult>("/api/admin/translations/backfill", {
        method: "POST",
        token,
    });
}

// Admin: Ansichten
export interface StandingRow {
    text: string;
    count: number;
}

export interface ResultRow {
    id: number;
    display_text: string;
    position: number;
    votes: number;
}

export async function fetchStandings(token: string, categoryId: number): Promise<StandingRow[]> {
    return apiRequest<StandingRow[]>(`/api/admin/categories/${categoryId}/standings`, { token });
}

export async function fetchResults(token: string, categoryId: number): Promise<ResultRow[]> {
    return apiRequest<ResultRow[]>(`/api/admin/categories/${categoryId}/results`, { token });
}

// Admin: Aktionen
export async function setFinalists(token: string, categoryId: number, finalists: string[]) {
    return apiRequest(`/api/admin/categories/${categoryId}/finalists`, {
        method: "PUT",
        body: { finalists },
        token,
    });
}

export interface FinalistSuggestion {
    position: number;
    display_text: string;
    count: number;
}

export interface FinalistSuggestionResult {
    suggestions: FinalistSuggestion[];
    tieAtCutoff: boolean;
    tiedCount: number | null;
    totalEntries: number;
}

export async function fetchFinalistSuggestion(
    token: string,
    categoryId: number
): Promise<FinalistSuggestionResult> {
    return apiRequest<FinalistSuggestionResult>(
        `/api/admin/categories/${categoryId}/finalists/suggestion`,
        { token }
    );
}

export interface AdminFinalist {
    display_text: string;
    position: number;
}

export async function fetchAdminFinalists(
    token: string,
    categoryId: number
): Promise<AdminFinalist[]> {
    return apiRequest<AdminFinalist[]>(
        `/api/admin/categories/${categoryId}/finalists`,
        { token }
    );
}

// Admin: Ergebnisse veroeffentlichen
export interface RankingRow {
    rank: number;
    display_text: string;
    points: number;
}

export async function publishRanking(token: string, categoryId: number): Promise<{ message: string }> {
    return apiRequest<{ message: string }>(`/api/admin/categories/${categoryId}/ranking/publish`, {
        method: "POST",
        token,
    });
}

export async function unpublishRanking(token: string, categoryId: number): Promise<{ message: string }> {
    return apiRequest<{ message: string }>(`/api/admin/categories/${categoryId}/ranking/unpublish`, {
        method: "POST",
        token,
    });
}

// Admin: Ergebnis-Entwurf
export interface RankingDraftRow {
    rank: number;
    display_text: string;
    points: number;
    game: string | null;
    image_url: string | null;
    description: string | null;
    published: boolean;
}

export async function takeOverResults(token: string, categoryId: number): Promise<RankingDraftRow[]> {
    return apiRequest<RankingDraftRow[]>(`/api/admin/categories/${categoryId}/ranking/take-over`, {
        method: "POST",
        token,
    });
}

export async function getRankingDraft(token: string, categoryId: number): Promise<RankingDraftRow[]> {
    return apiRequest<RankingDraftRow[]>(`/api/admin/categories/${categoryId}/ranking`, { token });
}

export async function updateRankingEntry(
    token: string,
    categoryId: number,
    rank: number,
    game: string,
    image_url: string
): Promise<RankingDraftRow> {
    return apiRequest<RankingDraftRow>(`/api/admin/categories/${categoryId}/ranking/${rank}`, {
        method: "PUT",
        body: { game, image_url },
        token,
    });
}

export async function saveManualWinner(
    token: string,
    categoryId: number,
    payload: {
        display_text: string;
        game: string;
        image_url: string;
        description: string;
    }
): Promise<RankingDraftRow> {
    return apiRequest<RankingDraftRow>(`/api/admin/categories/${categoryId}/ranking/manual-winner`, {
        method: "PUT",
        body: payload,
        token,
    });
}

// Oeffentlich: veroeffentlichte Ranglisten
export interface RankingEntry {
    rank: number;
    display_text: string;
    points: number;
    game: string | null;
    image_url: string | null;
    description: string | null;
}

export interface CategoryRanking {
    categoryId: number;
    slug: string;
    name: string;
    group_key?: string;
    entries: RankingEntry[];
}

export async function fetchAllRankings(lang?: string): Promise<CategoryRanking[]> {
    const q = lang ? `?lang=${encodeURIComponent(lang)}` : "";
    return apiRequest<CategoryRanking[]>(`/api/rankings${q}`);
}

// Oeffentlich: Voting
export interface PublicFinalist {
    id: number;
    display_text: string;
    position: number;
}

export async function fetchPublicFinalists(categoryId: number): Promise<PublicFinalist[]> {
    return apiRequest<PublicFinalist[]>(`/api/categories/${categoryId}/finalists`);
}

export async function castVote(categoryId: number, finalistId: number, securityToken: string) {
    return apiRequest(`/api/categories/${categoryId}/vote`, {
        method: "POST",
        body: { finalistId, securityToken },
    });
}

export async function fetchMyVote(categoryId: number): Promise<{ finalistId: number | null }> {
    return apiRequest(`/api/categories/${categoryId}/my-vote`);
}

// Business-Case-Zeitplan
export interface BusinessCaseWindow {
    starts_at: string | null;
    deadline: string | null;
    updated_at?: string | null;
}

export async function fetchBusinessCaseDeadline(): Promise<BusinessCaseWindow> {
    return apiRequest<BusinessCaseWindow>("/api/business-case/deadline");
}

export async function setBusinessCaseWindow(
    token: string,
    window: { starts_at: string | null; deadline: string | null }
) {
    return apiRequest("/api/admin/business-case/deadline", {
        method: "PUT",
        body: window,
        token,
    });
}

// Admin: CSV-Export herunterladen
export async function downloadAdminCsv(token: string, path: string, filename: string): Promise<void> {
    return apiDownload(path, filename, { token });
}

// Admin: Kategorien verwalten
export async function fetchAdminCategories(token: string): Promise<AdminCategory[]> {
    return apiRequest<AdminCategory[]>("/api/admin/categories", { token });
}

export async function createCategory(token: string, input: CategoryInput): Promise<Category> {
    return apiRequest<Category>("/api/admin/categories", { method: "POST", body: input, token });
}

export async function updateCategory(token: string, id: number, input: CategoryInput): Promise<Category> {
    return apiRequest<Category>(`/api/admin/categories/${id}`, { method: "PUT", body: input, token });
}

export async function deleteCategory(token: string, id: number): Promise<{ message: string }> {
    return apiRequest<{ message: string }>(`/api/admin/categories/${id}`, { method: "DELETE", token });
}

// Holt eine einzelne Kategorie per Slug.
export async function fetchCategoryBySlug(slug: string, lang?: string): Promise<Category | null> {
    const cats = await fetchCategories(lang);
    return cats.find((c) => c.slug === slug) ?? null;
}

// Startseiten-Inhalte
export async function fetchSiteContent(lang?: string): Promise<Record<string, string>> {
    const q = lang ? `?lang=${encodeURIComponent(lang)}` : "";
    return apiRequest<Record<string, string>>(`/api/content${q}`);
}

export async function updateSiteContent(
    token: string,
    entries: Record<string, string>
): Promise<Record<string, string>> {
    return apiRequest<Record<string, string>>("/api/admin/content", {
        method: "PUT",
        body: entries,
        token,
    });
}

// News-/Social-Karten
export type NewsCardKind = "manual" | "instagram";

export interface NewsCard {
    id: number;
    position: number;
    is_visible: boolean;
    kind: NewsCardKind;
    title: string;
    description?: string | null;
    image_url?: string | null;
    link_url?: string | null;
    link_label?: string | null;
    instagram_url?: string | null;
    created_at?: string;
    updated_at?: string;
}

export interface NewsCardInput {
    position: number;
    is_visible: boolean;
    kind: NewsCardKind;
    title: string;
    description?: string | null;
    image_url?: string | null;
    link_url?: string | null;
    link_label?: string | null;
    instagram_url?: string | null;
}

type NewsCardResponse = Omit<NewsCard, "id" | "position" | "is_visible"> & {
    id: number | string;
    position: number | string;
    is_visible: boolean | string;
};

type NewsCardsResponse = { items: NewsCardResponse[] } | NewsCardResponse[];
type NewsCardMutationResponse = { item?: NewsCardResponse } | NewsCardResponse;

function normalizeNewsCard(card: NewsCardResponse): NewsCard {
    return {
        ...card,
        id: toPositiveInteger(card.id, "News-Karten-ID"),
        position: toPositiveInteger(card.position, "Position"),
        is_visible: card.is_visible === true || card.is_visible === "true",
    };
}

function normalizeNewsCards(result: NewsCardsResponse): NewsCard[] {
    const items = Array.isArray(result) ? result : result.items;
    return items.map(normalizeNewsCard);
}

function normalizeNewsCardResult(result: NewsCardMutationResponse): NewsCard {
    if ("item" in result) {
        if (!result.item) throw new Error("News-Karte konnte nicht gelesen werden.");
        return normalizeNewsCard(result.item);
    }
    return normalizeNewsCard(result as NewsCardResponse);
}

export async function fetchNewsCards(): Promise<NewsCard[]> {
    const result = await apiRequest<NewsCardsResponse>("/api/news-cards");
    return normalizeNewsCards(result);
}

export async function fetchAdminNewsCards(token: string): Promise<NewsCard[]> {
    const result = await apiRequest<NewsCardsResponse>("/api/admin/news-cards", { token });
    return normalizeNewsCards(result);
}

export async function createAdminNewsCard(
    token: string,
    payload: NewsCardInput
): Promise<NewsCard> {
    const result = await apiRequest<NewsCardMutationResponse>(
        "/api/admin/news-cards",
        { method: "POST", body: payload, token }
    );
    return normalizeNewsCardResult(result);
}

export async function updateAdminNewsCard(
    token: string,
    id: number,
    payload: NewsCardInput
): Promise<NewsCard> {
    const normalizedId = toPositiveInteger(id, "News-Karten-ID");
    const result = await apiRequest<NewsCardMutationResponse>(
        `/api/admin/news-cards/${normalizedId}`,
        { method: "PATCH", body: payload, token }
    );
    return normalizeNewsCardResult(result);
}

export async function deleteAdminNewsCard(token: string, id: number): Promise<void> {
    const normalizedId = toPositiveInteger(id, "News-Karten-ID");
    await apiRequest<void>(`/api/admin/news-cards/${normalizedId}`, {
        method: "DELETE",
        token,
    });
}

// Admin: Nominierungen zusammenfuehren
export interface NominationEntry {
    normalized_text: string;
    display_text: string;
    count: number;
}

export async function fetchAllEntries(token: string, categoryId: number): Promise<NominationEntry[]> {
    return apiRequest<NominationEntry[]>(`/api/admin/categories/${categoryId}/all-entries`, { token });
}

export async function mergeNominations(token: string, categoryId: number, from: string, into: string) {
    return apiRequest(`/api/admin/categories/${categoryId}/merge`, {
        method: "POST",
        body: { from, into },
        token,
    });
}

export async function mergeNominationsBulk(token: string, categoryId: number, from: string[], into: string) {
    return apiRequest(`/api/admin/categories/${categoryId}/merge-bulk`, {
        method: "POST",
        body: { from, into },
        token,
    });
}

export interface DeleteNominationEntriesResult {
    deleted?: number;
    blocked?: number | string[];
}

export async function deleteNominationEntries(
    token: string,
    categoryId: number,
    normalizedTexts: string[],
    block: boolean
): Promise<DeleteNominationEntriesResult> {
    return apiRequest<DeleteNominationEntriesResult>(`/api/admin/categories/${categoryId}/nomination-entries/delete`, {
        method: "POST",
        body: { normalizedTexts, block },
        token,
    });
}

export interface BlockNominationEntriesResult {
    message: string;
    blocked: string[];
    alreadyBlocked: number;
}

export async function blockNominationEntries(
    token: string,
    categoryId: number,
    normalizedTexts: string[],
    reason?: string | null
): Promise<BlockNominationEntriesResult> {
    return apiRequest<BlockNominationEntriesResult>(
        `/api/admin/categories/${categoryId}/nomination-entries/block`,
        {
            method: "POST",
            body: { normalizedTexts, reason: reason ?? null },
            token,
        }
    );
}

// Admin: Blockierte Nominierungen verwalten
export interface NominationBlocklistEntry {
    id: number;
    category_id: number | null;
    normalized_text: string;
    display_text: string;
    reason: string | null;
    created_at: string;
    scope: "global" | "category";
}

export interface UpdateNominationBlocklistEntryPayload {
    displayText?: string;
    reason?: string | null;
}

export interface UnblockNominationBlocklistEntriesResult {
    message: string;
    deletedIds: number[];
    deletedCount: number;
}

type NominationBlocklistEntryResponse = Omit<
    NominationBlocklistEntry,
    "id" | "category_id"
> & {
    id: number | string;
    category_id: number | string | null;
};

type UnblockNominationBlocklistEntriesResponse = Omit<
    UnblockNominationBlocklistEntriesResult,
    "deletedIds"
> & {
    deletedIds: Array<number | string>;
};

function toPositiveInteger(value: unknown, label: string): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) {
        throw new Error(`${label} ist ungueltig.`);
    }
    return parsed;
}

function normalizeNominationBlocklistEntry(
    entry: NominationBlocklistEntryResponse
): NominationBlocklistEntry {
    return {
        ...entry,
        id: toPositiveInteger(entry.id, "Blocklist-ID"),
        category_id: entry.category_id === null
            ? null
            : toPositiveInteger(entry.category_id, "Kategorie-ID"),
    };
}

export async function fetchNominationBlocklist(
    token: string,
    categoryId: number,
    search?: string
): Promise<NominationBlocklistEntry[]> {
    const normalizedCategoryId = toPositiveInteger(categoryId, "Kategorie-ID");
    const query = search?.trim()
        ? `?search=${encodeURIComponent(search.trim())}`
        : "";
    const result = await apiRequest<{ items: NominationBlocklistEntryResponse[] }>(
        `/api/admin/categories/${normalizedCategoryId}/nomination-blocklist${query}`,
        { token }
    );
    return result.items.map(normalizeNominationBlocklistEntry);
}

export async function updateNominationBlocklistEntry(
    token: string,
    id: number,
    payload: UpdateNominationBlocklistEntryPayload
): Promise<NominationBlocklistEntry> {
    const normalizedId = toPositiveInteger(id, "Blocklist-ID");
    const result = await apiRequest<{ item: NominationBlocklistEntryResponse }>(
        `/api/admin/nomination-blocklist/${normalizedId}`,
        {
            method: "PATCH",
            body: payload,
            token,
        }
    );
    return normalizeNominationBlocklistEntry(result.item);
}

export async function unblockNominationBlocklistEntries(
    token: string,
    categoryId: number,
    ids: number[],
    includeKnownAliases = false
): Promise<UnblockNominationBlocklistEntriesResult> {
    const normalizedCategoryId = toPositiveInteger(categoryId, "Kategorie-ID");
    const normalizedIds = ids.map((id) => toPositiveInteger(id, "Blocklist-ID"));
    const result = await apiRequest<UnblockNominationBlocklistEntriesResponse>(
        `/api/admin/categories/${normalizedCategoryId}/nomination-blocklist/unblock`,
        {
            method: "POST",
            body: { ids: normalizedIds, includeKnownAliases },
            token,
        }
    );
    return {
        ...result,
        deletedIds: result.deletedIds.map((id) =>
            toPositiveInteger(id, "Geloeschte Blocklist-ID")
        ),
    };
}

// Admin: Geloeschte Nominierungen verwalten
export type DeletedNominationAction = "delete_only" | "delete_and_block";

export interface DeletedNominationEntry {
    id: number;
    category_id: number;
    normalized_text: string;
    display_text: string;
    count: number;
    reason?: string | null;
    deleted_action: DeletedNominationAction;
    was_blocked: boolean;
    currently_blocked: boolean;
    raw_nominations?: unknown;
    submission_records?: unknown;
    created_at: string;
    restored: boolean;
    restored_at?: string | null;
}

type DeletedNominationEntryResponse = Omit<
    DeletedNominationEntry,
    "id" | "category_id" | "count"
> & {
    id: number | string;
    category_id: number | string;
    count: number | string;
};

type DeletedNominationEntriesResponse =
    | { items: DeletedNominationEntryResponse[] }
    | DeletedNominationEntryResponse[];

function toNonNegativeInteger(value: unknown, label: string): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 0) {
        throw new Error(`${label} ist ungueltig.`);
    }
    return parsed;
}

function toBoolean(value: unknown): boolean {
    return value === true || value === "true";
}

function normalizeDeletedNominationAction(value: unknown): DeletedNominationAction {
    return value === "delete_and_block" ? "delete_and_block" : "delete_only";
}

function normalizeDeletedNominationEntry(
    entry: DeletedNominationEntryResponse
): DeletedNominationEntry {
    return {
        ...entry,
        id: toPositiveInteger(entry.id, "Archiv-ID"),
        category_id: toPositiveInteger(entry.category_id, "Kategorie-ID"),
        count: toNonNegativeInteger(entry.count, "Anzahl"),
        deleted_action: normalizeDeletedNominationAction(entry.deleted_action),
        was_blocked: toBoolean(entry.was_blocked),
        currently_blocked: toBoolean(entry.currently_blocked),
        restored: toBoolean(entry.restored),
    };
}

function normalizeDeletedNominationItems(
    result: DeletedNominationEntriesResponse
): DeletedNominationEntry[] {
    const items = Array.isArray(result) ? result : result.items;
    return items.map(normalizeDeletedNominationEntry);
}

export async function fetchDeletedNominationEntries(
    token: string,
    categoryId: number,
    search?: string
): Promise<DeletedNominationEntry[]> {
    const normalizedCategoryId = toPositiveInteger(categoryId, "Kategorie-ID");
    const query = search?.trim()
        ? `?search=${encodeURIComponent(search.trim())}`
        : "";
    const result = await apiRequest<DeletedNominationEntriesResponse>(
        `/api/admin/categories/${normalizedCategoryId}/deleted-nomination-entries${query}`,
        { token }
    );
    return normalizeDeletedNominationItems(result);
}

export async function restoreDeletedNominationEntry(
    token: string,
    id: number
): Promise<void> {
    const normalizedId = toPositiveInteger(id, "Archiv-ID");
    await apiRequest<unknown>(
        `/api/admin/deleted-nomination-entries/${normalizedId}/restore`,
        { method: "POST", token }
    );
}

export async function blockDeletedNominationEntry(
    token: string,
    id: number
): Promise<void> {
    const normalizedId = toPositiveInteger(id, "Archiv-ID");
    await apiRequest<unknown>(
        `/api/admin/deleted-nomination-entries/${normalizedId}/block`,
        { method: "POST", token }
    );
}

export async function deleteDeletedNominationArchiveEntry(
    token: string,
    id: number
): Promise<void> {
    const normalizedId = toPositiveInteger(id, "Archiv-ID");
    await apiRequest<void>(
        `/api/admin/deleted-nomination-entries/${normalizedId}`,
        { method: "DELETE", token }
    );
}